// lib/machineCatalog.js
// R7 퍼포먼스 머신 기록 입력 화면의 카탈로그와 입력 계산. 계약: docs/architecture/app-v2/R6b-R7.md "# R7"
//   2.1 메트릭 카탈로그(머신 5종 × 메트릭 13개, 표시명·단위·소수 자릿수·낮을수록 좋음), 2.4 admin-web 입력, 6.6 입력 허용 범위
//   - 숫자는 서버 카탈로그(cloudapi/internal/machines/catalog.go)와 같아야 한다. 서버 응답에 카탈로그가 없어 여기 한 곳에 두고
//     scripts/test-machine-records.mjs가 설계 문서 표(2.1·6.6)와 맞춰 본다. 범위·자릿수를 바꾸면 서버와 함께 고친다.
//   - 시간 메트릭(time_500m·time_400m)은 초로 주고받고, 입력은 "1:48.2"와 "108.2" 둘 다 받는다. 표시는 프로토타입 sec2("1:48.2")
//   - 소수 자릿수를 넘는 입력은 반올림하지 않고 오류로 본다(2.4). 끝의 0("106.0")은 값이 그대로라 받는다
//   - 빈 칸 = 기록 없음. 있던 값을 지우면 삭제(value null)로 보낸다
// 이 파일은 import가 없다(scripts/test-machine-records.mjs가 node로 그대로 불러 시험한다).

// 등급명(프로토타입 PRTIER). grade_changes의 label이 비었을 때만 쓴다
export const TIER_LABELS = Object.freeze([
  "입문",
  "초급",
  "중급",
  "상급",
  "최상급",
]);

function metric(key, label, unit, decimals, min, max, extra = {}) {
  return Object.freeze({
    key,
    label,
    unit,
    decimals,
    min,
    max,
    lowerIsBetter: !!extra.lowerIsBetter,
    time: !!extra.time,
  });
}

// 시간 메트릭(초, 소수 1자리, 낮을수록 좋음). 단위 글자는 없고 "m:ss.s"로 보인다
function timeMetric(key, label, min, max) {
  return metric(key, label, "", 1, min, max, {
    lowerIsBetter: true,
    time: true,
  });
}

// 순서(머신·메트릭)는 설계 2.1 표 순서. 입력 화면 열 순서도 같다. primary = 등급 기준 메트릭(기본 열)
export const MACHINES = Object.freeze([
  Object.freeze({
    key: "airbike",
    name: "에어바이크",
    primary: "max_rpm",
    metrics: Object.freeze([metric("max_rpm", "Max RPM", "RPM", 0, 20, 200)]),
  }),
  Object.freeze({
    key: "ski",
    name: "스키 에르그",
    primary: "max_cal_per_hour",
    metrics: Object.freeze([
      metric("max_cal_per_hour", "Max cal/h", "cal/h", 0, 100, 5000),
      metric("dist_140", "1:40 Distance", "m", 0, 100, 900),
      timeMetric("time_500m", "500m", 60, 300),
    ]),
  }),
  Object.freeze({
    key: "row",
    name: "로우 에르그",
    primary: "max_cal_per_hour",
    metrics: Object.freeze([
      metric("max_cal_per_hour", "Max cal/h", "cal/h", 0, 100, 5000),
      metric("dist_140", "1:40 Distance", "m", 0, 100, 900),
      timeMetric("time_500m", "500m", 60, 300),
    ]),
  }),
  Object.freeze({
    key: "bike",
    name: "바이크 에르그",
    primary: "max_cal_per_hour",
    metrics: Object.freeze([
      metric("max_cal_per_hour", "Max cal/h", "cal/h", 0, 100, 6000),
      metric("dist_140", "1:40 Distance", "m", 0, 200, 2000),
      timeMetric("time_500m", "500m", 30, 150),
    ]),
  }),
  Object.freeze({
    key: "treadmill",
    name: "무동력 트레드밀",
    primary: "max_kmh",
    metrics: Object.freeze([
      metric("max_kmh", "Max km/h", "km/h", 1, 5, 45),
      metric("dist_140", "1:40 Distance", "m", 0, 100, 1000),
      timeMetric("time_400m", "400m", 40, 200),
    ]),
  }),
]);

export const MACHINE_KEYS = Object.freeze(MACHINES.map((m) => m.key));

export function machineByKey(key) {
  return MACHINES.find((m) => m.key === key) || null;
}

export function metricOf(machineKey, metricKey) {
  const m = machineByKey(machineKey);
  return (m && m.metrics.find((x) => x.key === metricKey)) || null;
}

// 칸 키 "airbike.max_rpm"
export function recordKey(machineKey, metricKey) {
  return `${machineKey}.${metricKey}`;
}

// 카탈로그의 모든 칸(13개, 2.1 표 순서)
export const ALL_COLUMNS = Object.freeze(
  MACHINES.flatMap((m) =>
    m.metrics.map((x) =>
      Object.freeze({
        key: recordKey(m.key, x.key),
        machine: m.key,
        machineName: m.name,
        metric: x,
        primary: x.key === m.primary,
      })
    )
  )
);

export function columnByKey(key) {
  return ALL_COLUMNS.find((c) => c.key === key) || null;
}

// 세부 기록(dist_140·time_500m/time_400m)이 있는 머신인가. 에어바이크는 기준 메트릭 하나뿐이다(2.1)
export function hasDetailMetrics(machineKey) {
  const m = machineByKey(machineKey);
  return !!m && m.metrics.length > 1;
}

// 보이는 열: 고른 머신의 기준 메트릭, showDetail이면 세부 메트릭까지. 카탈로그 순서
export function visibleColumns(selectedMachines, showDetail) {
  const picked = new Set(
    Array.isArray(selectedMachines) ? selectedMachines : []
  );
  return ALL_COLUMNS.filter(
    (c) => picked.has(c.machine) && (c.primary || !!showDetail)
  );
}

// 초 → "m:ss.s"(프로토타입 sec2). 108.2 → "1:48.2", 60 → "1:00.0". 부동소수 오차 없게 0.1초 단위 정수로 계산한다
export function formatSeconds(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return "";
  const tenths = Math.round(n * 10);
  const minutes = Math.floor(tenths / 600);
  const rest = tenths - minutes * 600;
  const sec = Math.floor(rest / 10);
  return `${minutes}:${String(sec).padStart(2, "0")}.${rest % 10}`;
}

// 저장된 값 → 입력칸 글자. 시간은 "1:48.2", 그 밖은 자릿수 안에서 짧게(18 → "18", 17.5 → "17.5")
export function formatMetricValue(metricDef, value) {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  if (metricDef && metricDef.time) return formatSeconds(n);
  const d = metricDef ? metricDef.decimals : 1;
  return String(Number(n.toFixed(d)));
}

// 범위 안내 "20~200 RPM", "5~45 km/h", 시간은 "1:00.0~5:00.0 (60~300초)"
export function rangeHint(metricDef) {
  if (!metricDef) return "";
  if (metricDef.time) {
    return `${formatSeconds(metricDef.min)}~${formatSeconds(metricDef.max)} (${
      metricDef.min
    }~${metricDef.max}초)`;
  }
  return `${metricDef.min}~${metricDef.max}${
    metricDef.unit ? " " + metricDef.unit : ""
  }`;
}

// 범위 밖 오류 문구. 서버 항목별 오류(machines.Metric.RangeMessage)와 같은 글자: "20~200 RPM 사이로 입력해 주세요",
// 시간은 "60~300초 사이로 입력해 주세요"
export function rangeMessage(metricDef) {
  if (!metricDef) return "";
  const unit = metricDef.time
    ? "초"
    : metricDef.unit
    ? " " + metricDef.unit
    : "";
  return `${metricDef.min}~${metricDef.max}${unit} 사이로 입력해 주세요`;
}

const NUMBER_RE = /^(\d+)(?:\.(\d+))?$/;
const TIME_RE = /^(\d+):([0-5]\d)(?:\.(\d+))?$/;

// 소수 자리 글자에서 끝의 0을 뺀다("20" → "2", "0" → "")
function trimFraction(frac) {
  return String(frac || "").replace(/0+$/, "");
}

function decimalsError(metricDef) {
  return metricDef.decimals === 0
    ? "정수로 입력해 주세요"
    : `소수 ${metricDef.decimals}자리까지 입력해 주세요`;
}

// 입력칸 글자 → { empty: true, value: null } | { value } | { error }
//   - 앞뒤 공백은 무시한다. 쉼표·음수·지수·단위 글자는 받지 않는다("1,200"을 1.2로 읽지 않게)
//   - 시간 메트릭은 "m:ss(.s)"(초는 두 자리, 1:5 같은 애매한 글자는 거절) 또는 초("108.2")
//   - 자릿수 초과는 반올림하지 않고 오류, 범위(min·max 포함) 밖은 "20~200 RPM 사이로 입력해 주세요"(rangeMessage).
//     오류 문구는 서버 항목별 오류(cloudapi/internal/machines/catalog.go)와 같다
export function parseMetricInput(metricDef, raw) {
  const text = String(raw === null || raw === undefined ? "" : raw).trim();
  if (!text) return { empty: true, value: null };
  if (!metricDef) return { error: "알 수 없는 칸이에요" };
  const scale = 10 ** metricDef.decimals;
  let units = null; // 값 × 10^decimals (정수)
  let frac = "";
  const t = metricDef.time ? TIME_RE.exec(text) : null;
  if (t) {
    frac = trimFraction(t[3]);
    if (frac.length > metricDef.decimals)
      return { error: decimalsError(metricDef) };
    const seconds = Number(t[1]) * 60 + Number(t[2]);
    units = seconds * scale + Number(frac.padEnd(metricDef.decimals, "0") || 0);
  } else {
    const m = NUMBER_RE.exec(text);
    if (!m) {
      return {
        error: metricDef.time
          ? "1:48.2 또는 108.2처럼 입력해 주세요"
          : "숫자로 입력해 주세요",
      };
    }
    frac = trimFraction(m[2]);
    if (frac.length > metricDef.decimals)
      return { error: decimalsError(metricDef) };
    units =
      Number(m[1]) * scale + Number(frac.padEnd(metricDef.decimals, "0") || 0);
  }
  const value = units / scale;
  if (
    !Number.isFinite(value) ||
    value < metricDef.min ||
    value > metricDef.max
  ) {
    return { error: rangeMessage(metricDef) };
  }
  return { value };
}

// 두 값이 같은 기록인가(저장은 NUMERIC(7,1)이라 0.1 단위로 맞춰 본다. 서버가 숫자를 글자로 줘도 같게)
export function sameValue(a, b) {
  const x = Number(a);
  const y = Number(b);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  return Math.round(x * 10) === Math.round(y * 10);
}

// GET 3.3 한 예약의 records → { "airbike.max_rpm": 106, … }. 카탈로그에 없는 칸·숫자가 아닌 값은 버린다
export function originalFromRecords(records) {
  const out = {};
  (Array.isArray(records) ? records : []).forEach((r) => {
    if (!r || typeof r !== "object") return;
    const col = columnByKey(recordKey(r.machine, r.metric));
    const n = r.value === null || r.value === "" ? NaN : Number(r.value);
    if (col && Number.isFinite(n)) out[col.key] = n;
  });
  return out;
}

// 저장된 값 → 입력칸 글자 맵(모든 칸, 값 없으면 "")
export function draftFromOriginal(original) {
  const out = {};
  ALL_COLUMNS.forEach((c) => {
    const has =
      original && Object.prototype.hasOwnProperty.call(original, c.key);
    out[c.key] = has ? formatMetricValue(c.metric, original[c.key]) : "";
  });
  return out;
}

// 칸마다 검사 결과 { key: { value } | { empty } | { error } }
export function validateDraft(draft) {
  const out = {};
  ALL_COLUMNS.forEach((c) => {
    out[c.key] = parseMetricInput(c.metric, draft ? draft[c.key] : "");
  });
  return out;
}

// 한 예약의 바뀐 칸. records = POST 본문 항목(카탈로그 순서), deletes = 지우는 칸 수, errors = 오류 칸 키 목록.
// 오류 칸은 records에 넣지 않는다(오류가 하나라도 있으면 화면이 저장을 막는다)
export function diffRow(original, draft) {
  const orig = original || {};
  const records = [];
  const errors = [];
  let deletes = 0;
  ALL_COLUMNS.forEach((c) => {
    const parsed = parseMetricInput(c.metric, draft ? draft[c.key] : "");
    const had = Object.prototype.hasOwnProperty.call(orig, c.key);
    if (parsed.error) {
      errors.push(c.key);
      return;
    }
    if (parsed.empty) {
      if (had) {
        records.push({ machine: c.machine, metric: c.metric.key, value: null });
        deletes++;
      }
      return;
    }
    if (!had || !sameValue(orig[c.key], parsed.value)) {
      records.push({
        machine: c.machine,
        metric: c.metric.key,
        value: parsed.value,
      });
    }
  });
  return { records, deletes, errors };
}

// 지우기 전용 행(출석한 뒤 취소된 예약, row.deleteOnly)의 칸 문구. 서버는 값이 하나라도 있는 요청을 409로 거절한다
export const DELETE_ONLY_MESSAGE = "취소된 예약은 기록을 지우기만 할 수 있어요";

// 지우기 전용 행에서 원래 값과 다른 값을 넣은 칸 → { key: 문구 }. 비운 칸(지움)·원래 값 그대로인 칸·형식 오류 칸(validateDraft 몫)은 뺀다
export function deleteOnlyErrors(original, draft) {
  const orig = original || {};
  const out = {};
  ALL_COLUMNS.forEach((c) => {
    const parsed = parseMetricInput(c.metric, draft ? draft[c.key] : "");
    if (parsed.empty || parsed.error) return;
    const had = Object.prototype.hasOwnProperty.call(orig, c.key);
    if (!had || !sameValue(orig[c.key], parsed.value)) {
      out[c.key] = DELETE_ONLY_MESSAGE;
    }
  });
  return out;
}

// 화면 전체의 저장 계획. rows = [{ bookingId, deleteOnly }], originals·drafts = 예약 ID별 맵.
// requests = 바뀐 예약만 [{ bookingId, records }], changed = 바뀐 칸 수, deletes = 지우는 칸 수, errors = 오류 칸 수.
// 지우기 전용 행은 지운 칸(null)만 보내고, 값을 넣은 칸은 오류로 센다(저장을 막음)
export function planSave(rows, originals, drafts) {
  const requests = [];
  let changed = 0;
  let deletes = 0;
  let errors = 0;
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const id = row && row.bookingId;
    if (!id) return;
    const orig = originals ? originals[id] : null;
    const draft = drafts ? drafts[id] : null;
    const d = diffRow(orig, draft);
    let records = d.records;
    if (row.deleteOnly) {
      errors += Object.keys(deleteOnlyErrors(orig, draft)).length;
      records = records.filter((r) => r.value === null);
    }
    errors += d.errors.length;
    deletes += d.deletes;
    changed += records.length;
    if (records.length) requests.push({ bookingId: id, records });
  });
  return { requests, changed, deletes, errors };
}

// 저장 뒤 안내 줄 { kind, message }. 모두 실패하면 "0명은 저장했고 …" 대신 실패한 수만 말한다
export function saveNotice(ok, failed) {
  if (!failed) {
    return { kind: "success", message: `${ok}명의 머신 기록을 저장했어요.` };
  }
  if (!ok) {
    return {
      kind: "error",
      message: `${failed}명의 머신 기록을 저장하지 못했어요. 이름 아래 문구를 확인해 주세요.`,
    };
  }
  return {
    kind: "error",
    message: `${ok}명은 저장했고 ${failed}명은 저장하지 못했어요. 이름 아래 문구를 확인해 주세요.`,
  };
}

// 저장한 뒤의 원래 값. 응답의 records는 그 예약의 지금 기록 전부(서버가 커밋 뒤 다시 읽음)라 그것이 새 원래 값이다: 그사이 다른
// 관리자가 바꾸거나 넣거나 지운 칸까지 따른다. 예전에는 원래 값에 응답 값을 합치기만 해서 응답에 없는(지워진) 칸이 남았고, 폼이 보낸
// 칸만 다시 그려 다른 관리자의 새 값이 칸에는 옛 글자로 남아 다음 저장이 되돌리거나 지웠다(R7 전체 리뷰 1차). records가 없는
// 응답(배열이 아님, 옛 서버)이면 보낸 항목만 반영한다(value null = 지움)
export function applySaved(original, sentRecords, responseRecords) {
  if (Array.isArray(responseRecords))
    return originalFromRecords(responseRecords);
  const out = { ...(original || {}) };
  (Array.isArray(sentRecords) ? sentRecords : []).forEach((r) => {
    const key = recordKey(r.machine, r.metric);
    if (r.value === null) delete out[key];
    else out[key] = r.value;
  });
  return out;
}

// 400 항목별 오류(details.errors[]의 index = 보낸 records 위치) → { 칸 키: 문구 }. 위치가 틀리면 machine·metric으로 찾는다
export function itemErrorsByKey(sentRecords, itemErrors) {
  const sent = Array.isArray(sentRecords) ? sentRecords : [];
  const out = {};
  (Array.isArray(itemErrors) ? itemErrors : []).forEach((e) => {
    if (!e || typeof e !== "object") return;
    let key = "";
    if (e.machine && e.metric) key = recordKey(e.machine, e.metric);
    else if (Number.isInteger(e.index) && sent[e.index]) {
      key = recordKey(sent[e.index].machine, sent[e.index].metric);
    }
    if (key && columnByKey(key))
      out[key] = String(e.message || "값을 확인해 주세요");
  });
  return out;
}

// 칸 목록 중 저장하지 않은 입력이 있는 칸 수(머신을 숨기거나 세부 기록을 닫을 때 확인용)
export function pendingCountIn(keys, rows, originals, drafts) {
  const set = new Set(Array.isArray(keys) ? keys : []);
  let n = 0;
  (Array.isArray(rows) ? rows : []).forEach((row) => {
    const id = row && row.bookingId;
    if (!id) return;
    const orig = (originals && originals[id]) || {};
    const draft = (drafts && drafts[id]) || {};
    ALL_COLUMNS.forEach((c) => {
      if (!set.has(c.key)) return;
      const had = Object.prototype.hasOwnProperty.call(orig, c.key);
      const before = had ? formatMetricValue(c.metric, orig[c.key]) : "";
      const now = String(draft[c.key] === undefined ? "" : draft[c.key]).trim();
      if (now === before) return;
      const parsed = parseMetricInput(c.metric, now);
      if (parsed.error) n++;
      else if (
        parsed.empty ? had : !had || !sameValue(orig[c.key], parsed.value)
      )
        n++;
    });
  });
  return n;
}

// grade_changes 한 줄 → { up, text }. 내려간 등급도 보여 오입력을 알아채게 한다(설계 10절). label이 비면 등급명(0 = 서버 machines.NoTierLabel "입문 미만")
export function gradeChangeChip(change) {
  if (!change || typeof change !== "object") return null;
  const from = Number(change.from) || 0;
  const to = Number(change.to) || 0;
  if (from === to) return null;
  const m = machineByKey(change.machine);
  const label =
    (typeof change.label === "string" && change.label.trim()) ||
    (to >= 1 && to <= TIER_LABELS.length ? TIER_LABELS[to - 1] : "입문 미만");
  const up = to > from;
  return {
    up,
    text: `${m ? m.name + " " : ""}등급 ${up ? "↑" : "↓"} ${label}`,
  };
}

// pr 한 줄 → { text, title }. title = "101 → 106 RPM"(처음 기록이면 값만)
export function prChip(pr) {
  if (!pr || typeof pr !== "object") return null;
  const m = machineByKey(pr.machine);
  const def = metricOf(pr.machine, pr.metric);
  const fmt = (v) =>
    `${formatMetricValue(def, v)}${def && def.unit ? " " + def.unit : ""}`;
  const hasPrev =
    pr.previous_best !== null &&
    pr.previous_best !== undefined &&
    Number.isFinite(Number(pr.previous_best));
  return {
    // 기준 메트릭은 머신 이름만, 세부 메트릭은 표시명까지("PR 스키 에르그 500m")
    text: `PR ${m ? m.name : ""}${
      def && m && def.key !== m.primary ? " " + def.label : ""
    }`.trim(),
    title: hasPrev
      ? `${fmt(pr.previous_best)} → ${fmt(pr.value)}`
      : fmt(pr.value),
  };
}

// 실시간 심박 세션의 class_label → 수업 제목. class_label은 "{수업 시간} {타입 표시명}"이고 시간은 스케줄 API 그대로 "HH:MM:SS"다
// ("19:30:00 Pull & Cardio" → "Pull & Cardio"). 예전 대체 경로는 HH:MM만 지워 ":00 Pull & Cardio"가 폼 머리와 머신 선택 키에 남았다(R7 전체 리뷰 1차)
export function classTitleFromLabel(label) {
  return String(label || "")
    .trim()
    .replace(/^\d{1,2}:\d{2}(?::\d{2})?\s*/, "")
    .trim();
}

// 폼 머리의 수업 이름 "10월 18일 (일) 19:30 Pull & Cardio". 날짜가 YYYY-MM-DD가 아니면 날짜는 뺀다
export function formatClassLabel(ymd, time, title) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ""));
  let day = "";
  if (m) {
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
    const dow = ["일", "월", "화", "수", "목", "금", "토"][d.getUTCDay()];
    day = `${Number(m[2])}월 ${Number(m[3])}일 (${dow})`;
  }
  return [day, String(time || "").trim(), String(title || "").trim()]
    .filter(Boolean)
    .join(" ");
}

// 성별 표시. male/female 밖(NULL 포함)은 "" → 화면이 "성별 없음 · 등급 계산 안 됨" 경고를 띄운다(6.8)
export function genderLabel(gender) {
  if (gender === "male") return "남";
  if (gender === "female") return "여";
  return "";
}

// ---------- 머신 선택 기억(수업 제목별 localStorage) ----------

export const MACHINE_SELECTION_KEY_PREFIX = "machine-records-selection:";

export function selectionStorageKey(classTitle) {
  return MACHINE_SELECTION_KEY_PREFIX + String(classTitle || "").trim();
}

// 기억한 머신 목록. 저장소가 없거나 막혔거나(사생활 모드) 글자가 깨졌으면 []
export function loadMachineSelection(storage, classTitle) {
  if (!String(classTitle || "").trim()) return [];
  try {
    const raw = storage && storage.getItem(selectionStorageKey(classTitle));
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list)
      ? MACHINE_KEYS.filter((k) => list.includes(k))
      : [];
  } catch {
    return [];
  }
}

// 고른 머신을 기억한다. 실패해도 화면 동작은 그대로
export function saveMachineSelection(storage, classTitle, machines) {
  if (!String(classTitle || "").trim()) return;
  try {
    const list = MACHINE_KEYS.filter((k) => (machines || []).includes(k));
    if (storage)
      storage.setItem(selectionStorageKey(classTitle), JSON.stringify(list));
  } catch {
    // 저장소가 막혀도 무시한다
  }
}
